import type { PostgrestFilterBuilder } from '@supabase/postgrest-js'

import { LimitExceededError } from './error'

import type { DefaultSchema, Tables } from '~/apis/generated/database'
import { useSupabaseClient } from '~/composables/useSupabaseClient'
import {
  type CheckFile,
  type CheckFileWithLabels,
  type CheckFileCreateParams,
  type CheckFileUpdateParams,
  MAX_CHECK_FILES_COUNT,
} from '~/entities/checkFile'
import type { CheckFileLabel } from '~/entities/checkFileLabel'
import type { CheckFileLabelAssignment } from '~/entities/checkFileLabelAssignment'
import type { SortOptions } from '~/entities/supabase'

export type CheckFilesFilter = {
  locationId?: number
  searchKeyword?: string
  checkFileLabelIds?: CheckFileLabel['id'][]
  checkFileIds?: CheckFile['id'][]
  status?: CheckFile['status'][]
  type?: CheckFile['type'][]
  from?: number
  to?: number
}

async function createFilter<Q extends PostgrestFilterBuilder<any, DefaultSchema, Tables<'check_files'>, any>>(
  filter: CheckFilesFilter
): Promise<(query: Q) => Q> {
  // ラベルIDで絞り込む場合、該当ラベルが全て付与されているチェックファイルを取得する
  // クエリの Thenable と別個に非同期処理を実行するため、関数化して分離する
  const checkFileIdsByLabelIds: CheckFile['id'][] = [-1] // 空配列だとフィルタが効かなくなるため、存在しないIDを初期値に設定
  if (filter.checkFileLabelIds && filter.checkFileLabelIds.length > 0) {
    checkFileIdsByLabelIds.push(...(await getCheckFileIdsByLabelIds(filter.checkFileLabelIds)))
  }

  return (query: Q) => {
    if (filter.locationId) {
      query.eq('location_id', filter.locationId)
    }

    if (filter.checkFileLabelIds && filter.checkFileLabelIds.length > 0) {
      query.in('id', checkFileIdsByLabelIds)
    }

    if (filter.checkFileIds && filter.checkFileIds.length > 0) {
      query.in('id', filter.checkFileIds)
    }

    if (filter.searchKeyword) {
      query.ilike('title', `%${filter.searchKeyword}%`)
    }

    if (filter.status) {
      query.in('status', filter.status)
    }

    if (filter.type) {
      query.in('type', filter.type)
    }

    return query
  }
}
function rangeFilter<Q extends PostgrestFilterBuilder<any, DefaultSchema, Tables<'check_files'>, any>>(
  query: Q,
  range?: { from: number; to: number }
): Q {
  if (range) {
    return query.range(range.from, range.to)
  }
  return query
}

export const getCheckFiles = async (
  filter: CheckFilesFilter,
  range?: { from: number; to: number },
  sortOptions: SortOptions<CheckFile> = [{ column: 'id', order: 'asc' }]
): Promise<CheckFileWithLabels[]> => {
  const selectFilter = await createFilter(filter)

  let query = useSupabaseClient().from('check_files').select('*, check_file_labels(*)')
  query = selectFilter(query)
  query = rangeFilter(query, range)
  for (const { column, order } of sortOptions) {
    query = query.order(column, { ascending: order === 'asc' })
  }

  const { data, error } = await query
  if (error) throw error
  return data
}

const getCheckFileIdsByLabelIds = async (checkFileLabelIds: CheckFileLabel['id'][]): Promise<CheckFile['id'][]> => {
  const { data, error } = await useSupabaseClient()
    .from('check_file_label_assignments')
    .select('check_file_id')
    .in('check_file_label_id', checkFileLabelIds)
  if (error) throw error

  const countMap: Map<CheckFileLabelAssignment['check_file_id'], number> = new Map()
  data.forEach((item) => {
    const count = countMap.get(item.check_file_id) || 0
    countMap.set(item.check_file_id, count + 1)
  })

  return Array.from(countMap.entries())
    .filter(([, count]) => count === checkFileLabelIds.length)
    .map(([checkSheetId]) => checkSheetId)
}

export const createCheckFile = async (params: CheckFileCreateParams) => {
  const count = await getCheckFilesCount({ locationId: params.location_id })
  if (count >= MAX_CHECK_FILES_COUNT) throw new LimitExceededError()
  const { data, error } = await useSupabaseClient().from('check_files').insert(params).select('*').single()
  if (error) throw error
  return data
}

export const getCheckFile = async (id: CheckFile['id']) => {
  const { data, error } = await useSupabaseClient()
    .from('check_files')
    .select(
      '*, daily_verifiers(*, users(*)), monthly_verifiers(*, users(*)), check_sheet_assignments(*, ticket_sharing_groups(id, name), check_sheets(*, check_items(*, timeboxes(*), frequency_settings(*)))), check_file_labels(*), check_file_identifications(*)'
    )
    .eq('id', id)
    .order('position', { referencedTable: 'check_sheet_assignments', ascending: true })
    .maybeSingle()
  if (error) throw error
  return data
}

export const updateCheckFile = async (id: CheckFile['id'], params: CheckFileUpdateParams): Promise<void> => {
  const { error } = await useSupabaseClient().from('check_files').update(params).eq('id', id)
  if (error) throw error
}

export const deleteCheckFile = async (id: CheckFile['id']): Promise<void> => {
  const { error } = await useSupabaseClient().from('check_files').delete().eq('id', id)
  if (error) throw error
}

export const deleteCheckFiles = async (ids: CheckFile['id'][]): Promise<void> => {
  const { error } = await useSupabaseClient().from('check_files').delete().in('id', ids)
  if (error) throw error
}

export const getCheckFilesCount = async (filter: CheckFilesFilter = {}) => {
  const countFilter = await createFilter(filter)

  let query = useSupabaseClient().from('check_files').select('id', { count: 'exact', head: true })
  query = countFilter(query)

  const { count, error } = await query
  if (error) throw error
  return count ?? 0
}
